// src/Routes/CustomToolsLandingPage/CustomToolsLandingPage.tsx
import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { BsBraces, BsArrowRepeat, BsClockHistory } from "react-icons/bs";
import { FaTools, FaRobot, FaBrain } from "react-icons/fa";
import NavigationBar from "../../Components/HighLevelComponents/NavigationBar/NavigationBar";
import PageWrapper from "../../Components/Layout/PageWrapper/PageWrapper";

// Example image imports
const aiLandingImage =
  "https://res.cloudinary.com/dresq8wls/image/upload/v1746746108/ai-landing_zcn7zy.png";
const aiDemoImage =
  "https://res.cloudinary.com/dresq8wls/image/upload/v1746746108/ai-demo_vvdcdv.png";

// Modern button component with gradient
const GradientButton: React.FC<{
  onClick?: () => void;
  children: React.ReactNode;
  className?: string;
  secondary?: boolean;
  to?: string;
}> = ({ onClick, children, className = "", secondary = false, to }) => {
  const button = (
    <button
      onClick={onClick}
      className={`
        ${
          secondary
            ? "bg-white text-gray-800 border border-gray-200 hover:bg-gray-50"
            : "bg-gradient-to-r from-yellow-400 to-yellow-500 text-gray-900 hover:shadow-lg"
        }
        px-6 py-3 rounded-lg font-medium transition-all duration-200 transform hover:-translate-y-0.5
        ${className}
      `}
    >
      {children}
    </button>
  );

  if (to) {
    return (
      <Link to={to} className="no-underline">
        {button}
      </Link>
    );
  }

  return button;
};

// Feature card component
const FeatureCard: React.FC<{
  icon: React.ReactNode;
  title: string;
  description: string;
  delay?: number;
}> = ({ icon, title, description, delay = 0 }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5, delay: delay * 0.1 }}
    viewport={{ once: true }}
    className="bg-white rounded-xl p-8 shadow-sm hover:shadow-md transition-all duration-300 border border-gray-100"
  >
    <div className="text-yellow-500 mb-5 text-3xl">{icon}</div>
    <h3 className="text-xl font-bold text-gray-800 mb-3">{title}</h3>
    <p className="text-gray-600 leading-relaxed">{description}</p>
  </motion.div>
);

// Example card component
const ExampleCard: React.FC<{
  title: string;
  description: string;
  code: string;
  delay?: number;
}> = ({ title, description, code, delay = 0 }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5, delay: delay * 0.1 }}
    viewport={{ once: true }}
    className="bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-all duration-300 border border-gray-100"
  >
    <div className="p-6">
      <h3 className="text-xl font-bold text-gray-800 mb-2">{title}</h3>
      <p className="text-gray-600 mb-4">{description}</p>
      <div className="bg-gray-50 p-4 rounded-md border border-gray-200 text-sm font-mono text-gray-700 overflow-x-auto">
        {code}
      </div>
    </div>
  </motion.div>
);

// Testimonial component
const Testimonial: React.FC<{
  quote: string;
  author: string;
  role: string;
  delay?: number;
}> = ({ quote, author, role, delay = 0 }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5, delay: delay * 0.1 }}
    viewport={{ once: true }}
    className="bg-white rounded-xl p-8 shadow-sm hover:shadow transition-all duration-300 relative border border-gray-100 flex flex-col h-full"
  >
    <div className="absolute top-8 left-8 text-yellow-200 opacity-10">
      <svg width="40" height="40" fill="currentColor" viewBox="0 0 32 32">
        <path d="M9.352 4C4.456 7.456 1 13.12 1 19.36c0 5.088 3.072 8.064 6.624 8.064 3.36 0 5.856-2.688 5.856-5.856 0-3.168-2.208-5.472-5.088-5.472-.576 0-1.344.096-1.536.192.48-3.264 3.552-7.104 6.624-9.024L9.352 4zm16.512 0c-4.8 3.456-8.256 9.12-8.256 15.36 0 5.088 3.072 8.064 6.624 8.064 3.264 0 5.856-2.688 5.856-5.856 0-3.168-2.304-5.472-5.184-5.472-.576 0-1.248.096-1.44.192.48-3.264 3.456-7.104 6.528-9.024L25.864 4z" />
      </svg>
    </div>

    <p className="text-gray-700 mb-6 relative z-10 flex-grow">{quote}</p>

    <div className="flex items-center mt-auto">
      <div className="h-10 w-10 rounded-full bg-gradient-to-br from-yellow-400 to-yellow-500 flex items-center justify-center text-white font-bold flex-shrink-0">
        {author.charAt(0)}
      </div>
      <div className="ml-3">
        <p className="font-bold text-gray-800">{author}</p>
        <p className="text-gray-500 text-sm">{role}</p>
      </div>
    </div>
  </motion.div>
);

// FAQ item component
const FaqItem: React.FC<{
  question: string;
  answer: string;
  delay?: number;
}> = ({ question, answer, delay = 0 }) => {
  const [isOpen, setIsOpen] = React.useState(false);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: delay * 0.1 }}
      viewport={{ once: true }}
      className="border-b border-gray-200 pb-6"
    >
      <button
        className="flex justify-between items-center w-full text-left focus:outline-none"
        onClick={() => setIsOpen(!isOpen)}
      >
        <h3 className="text-lg font-medium text-gray-900">{question}</h3>
        <span className="ml-6 flex-shrink-0 text-gray-500">
          {isOpen ? (
            <svg
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 15l7-7 7 7"
              />
            </svg>
          ) : (
            <svg
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          )}
        </span>
      </button>
      {isOpen && (
        <div className="mt-3">
          <p className="text-base text-gray-600">{answer}</p>
        </div>
      )}
    </motion.div>
  );
};

// Step card component
const StepCard: React.FC<{
  number: number;
  title: string;
  description: string;
  delay?: number;
}> = ({ number, title, description, delay = 0 }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5, delay: delay * 0.1 }}
    viewport={{ once: true }}
    className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-all duration-300 border border-gray-100 relative"
  >
    <div className="absolute top-4 right-4 w-10 h-10 rounded-full bg-yellow-400 text-white flex items-center justify-center font-bold text-xl">
      {number}
    </div>
    <h3 className="text-xl font-bold text-gray-800 mb-3 pr-12">{title}</h3>
    <p className="text-gray-600">{description}</p>
  </motion.div>
);

// Main component
const CustomToolsLandingPage: React.FC = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Helmet>
        <title>
          Build Custom AI Tools Without Coding | Automate Tasks | Yippity
        </title>
        <meta
          name="description"
          content="Create your own custom AI tools without writing a single line of code. Automate repetitive tasks, save hours every week, and build your personalized AI toolkit with Yippity."
        />
        <meta
          name="keywords"
          content="ai tools, custom ai, no-code ai, ai automation, education technology, ai workflow, task automation, personalized ai"
        />
      </Helmet>

      <PageWrapper>
        <NavigationBar />

        {/* Hero Section */}
        <section className="relative overflow-hidden bg-gradient-to-b from-gray-50 to-white pt-20 pb-28">
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute left-[10%] top-[5%] h-64 w-64 rounded-full bg-yellow-300 opacity-20 mix-blend-multiply blur-3xl filter"></div>
            <div className="absolute right-[10%] top-[30%] h-72 w-72 rounded-full bg-yellow-400 opacity-20 mix-blend-multiply blur-3xl filter"></div>
          </div>

          <div className="container relative mx-auto px-4">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
              >
                <h1 className="text-4xl md:text-5xl font-bold text-gray-900 leading-tight">
                  Build Your Own{" "}
                  <span className="text-yellow-500">AI Tools</span> Without
                  Writing a Single Line of Code
                </h1>
                <p className="mt-6 text-xl text-gray-600 leading-relaxed">
                  Create powerful custom AI tools that automate your repetitive
                  tasks, save hours of work every week, and produce consistent
                  high-quality results exactly the way you want them.
                </p>
                <div className="mt-10 flex flex-wrap gap-4">
                  <GradientButton to="/signup">
                    Create Your First AI Tool
                  </GradientButton>
                  <GradientButton secondary to="/signup">
                    See How It Works
                  </GradientButton>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.8, delay: 0.3 }}
                className="relative"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-yellow-400 to-yellow-300 opacity-50 rounded-2xl transform rotate-3"></div>
                <div className="relative overflow-hidden rounded-xl shadow-2xl border-8 border-white">
                  <img
                    src={aiLandingImage}
                    alt="AI Tool Builder Interface"
                    className="w-full h-auto"
                  />
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="py-14 bg-white border-y border-gray-100">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <div className="text-4xl font-extrabold text-gray-800 mb-1">
                  5 min
                </div>
                <div className="text-gray-500 font-medium">
                  To Build Your First Tool
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <div className="text-4xl font-extrabold text-gray-800 mb-1">
                  10+
                </div>
                <div className="text-gray-500 font-medium">
                  Hrs Saved Weekly
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <div className="text-4xl font-extrabold text-gray-800 mb-1">
                  0
                </div>
                <div className="text-gray-500 font-medium">Coding Required</div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.3 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <div className="text-4xl font-extrabold text-gray-800 mb-1">
                  ∞
                </div>
                <div className="text-gray-500 font-medium">Possible Tools</div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-24 bg-gradient-to-b from-white to-gray-50">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="text-center max-w-3xl mx-auto mb-16"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-5">
                Create Once, Use Infinitely
              </h2>
              <p className="text-xl text-gray-600 leading-relaxed">
                Design custom AI tools that handle your repetitive tasks exactly
                how you want them, then use them with one click whenever you
                need them.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
              <FeatureCard
                icon={<FaTools />}
                title="No-Code Builder"
                description="Create sophisticated AI tools using our intuitive visual interface. No programming knowledge required—just tell the AI what you want it to do."
                delay={0}
              />

              <FeatureCard
                icon={<FaRobot />}
                title="Smart Automation"
                description="Transform multi-step processes into one-click operations. Build workflow-specific tools that save hours of repetitive work every week."
                delay={1}
              />

              <FeatureCard
                icon={<BsBraces />}
                title="Variable Inputs"
                description="Design tools that can accept different types of input—text, documents, URLs—making them flexible for any situation you encounter."
                delay={2}
              />

              <FeatureCard
                icon={<FaBrain />}
                title="Task Intelligence"
                description="Our AI understands complex instructions and executes them with precision, delivering consistent high-quality results every time."
                delay={3}
              />

              <FeatureCard
                icon={<BsArrowRepeat />}
                title="Reusable Workflows"
                description="Save your tools in your personal library and reuse them anytime. Share them with colleagues for consistent team-wide processes."
                delay={4}
              />

              <FeatureCard
                icon={<BsClockHistory />}
                title="Time-Saving Results"
                description="Get results in seconds that would take hours to produce manually. Specify exactly what outputs you want for maximum efficiency."
                delay={5}
              />
            </div>
          </div>
        </section>

        {/* How It Works Section */}
        <section className="py-24 bg-white">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="text-center max-w-3xl mx-auto mb-16"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-5">
                How to Build Your Custom AI Tools
              </h2>
              <p className="text-xl text-gray-600 leading-relaxed">
                Creating your own AI-powered tool is simple and intuitive—no
                technical skills required.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
              <StepCard
                number={1}
                title="Name Your Tool"
                description="Start by giving your tool a descriptive name that reflects what it does."
                delay={0}
              />

              <StepCard
                number={2}
                title="Write Instructions"
                description="Tell the AI exactly what you want it to do using clear, step-by-step directions."
                delay={1}
              />

              <StepCard
                number={3}
                title="Define Variables"
                description="Specify what information your tool needs from users to do its job."
                delay={2}
              />

              <StepCard
                number={4}
                title="Set Results Format"
                description="Choose how you want the AI to present its results for maximum usefulness."
                delay={3}
              />
            </div>

            <div className="mt-16 text-center">
              <GradientButton to="/signup">Start Building Now</GradientButton>
            </div>
          </div>
        </section>

        {/* Example Use Cases Section */}
        <section className="py-24 bg-gradient-to-b from-gray-50 to-white">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="text-center max-w-3xl mx-auto mb-16"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-5">
                What Will You Build?
              </h2>
              <p className="text-xl text-gray-600 leading-relaxed">
                Explore the endless possibilities of custom AI tools for
                education and beyond.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
              <ExampleCard
                title="Personalized Feedback Generator"
                description="Create a tool that provides tailored feedback on student assignments based on your rubric and teaching style."
                code={`Tool Name: Assignment Feedback Generator

Instructions:
Analyze the {{student_work}} based on the {{rubric}}.
Provide detailed feedback focusing on {{feedback_focus}} with a {{tone}} tone.
Include {{num_points}} specific points for improvement.

Variables:
- student_work (text/document)
- rubric (text)
- feedback_focus (selection)
- tone (selection)
- num_points (number)`}
                delay={0}
              />

              <ExampleCard
                title="Custom Lesson Plan Creator"
                description="Design a tool that generates complete lesson plans aligned to your curriculum standards and teaching methodology."
                code={`Tool Name: Lesson Plan Generator

Instructions:
Create a lesson plan for {{subject}} at {{grade_level}} about {{topic}}.
Include {{lesson_components}} and ensure it aligns with {{standards}}.
Make it suitable for a {{duration}} minute class.

Variables:
- subject (text)
- grade_level (selection)
- topic (text)
- lesson_components (selection)
- standards (text)
- duration (number)`}
                delay={1}
              />

              <ExampleCard
                title="Research Paper Summarizer"
                description="Build a tool that extracts key information from academic papers and research documents with your preferred structure."
                code={`Tool Name: Research Summarizer

Instructions:
Analyze the {{research_paper}} and create a summary.
Focus on {{focus_areas}} with {{summary_length}} paragraphs.
Include a section on {{special_focus}} if present in the paper.

Variables:
- research_paper (text/document/URL)
- focus_areas (text)
- summary_length (number)
- special_focus (text)`}
                delay={2}
              />

              <ExampleCard
                title="Data Analysis Assistant"
                description="Create a tool that extracts insights from complex data sets and presents them in your preferred format."
                code={`Tool Name: Data Insights Generator

Instructions:
Analyze the {{data_description}} focusing on {{analysis_focus}}.
Identify {{num_insights}} key insights.
Format the analysis with {{format_preferences}} for easy understanding.

Variables:
- data_description (text/document)
- analysis_focus (text)
- num_insights (number)
- format_preferences (selection)`}
                delay={3}
              />
            </div>
          </div>
        </section>

        {/* Demo Section */}
        <section className="py-24 bg-white relative overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute left-[5%] bottom-[10%] h-64 w-64 rounded-full bg-yellow-300 opacity-10 mix-blend-multiply blur-3xl filter"></div>
            <div className="absolute right-[15%] top-[20%] h-72 w-72 rounded-full bg-yellow-400 opacity-10 mix-blend-multiply blur-3xl filter"></div>
          </div>

          <div className="container mx-auto px-4 relative">
            <div className="grid md:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <h2 className="text-3xl font-bold text-gray-900 mb-6">
                  See Custom AI Tools in Action
                </h2>
                <p className="text-lg text-gray-600 leading-relaxed mb-6">
                  Watch how easy it is to create a powerful custom AI tool in
                  just minutes, and then use it repeatedly to save hours of
                  work.
                </p>
                <ul className="space-y-4">
                  <li className="flex items-start">
                    <div className="flex-shrink-0 h-6 w-6 rounded-full bg-green-100 flex items-center justify-center text-green-500 mt-0.5 mr-3">
                      <svg
                        className="h-4 w-4"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                    <span className="text-gray-700">
                      Design your tool with our intuitive builder
                    </span>
                  </li>
                  <li className="flex items-start">
                    <div className="flex-shrink-0 h-6 w-6 rounded-full bg-green-100 flex items-center justify-center text-green-500 mt-0.5 mr-3">
                      <svg
                        className="h-4 w-4"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                    <span className="text-gray-700">
                      Save it to your personal tool library
                    </span>
                  </li>
                  <li className="flex items-start">
                    <div className="flex-shrink-0 h-6 w-6 rounded-full bg-green-100 flex items-center justify-center text-green-500 mt-0.5 mr-3">
                      <svg
                        className="h-4 w-4"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                    <span className="text-gray-700">
                      Run it with one click whenever you need it
                    </span>
                  </li>
                  <li className="flex items-start">
                    <div className="flex-shrink-0 h-6 w-6 rounded-full bg-green-100 flex items-center justify-center text-green-500 mt-0.5 mr-3">
                      <svg
                        className="h-4 w-4"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                    <span className="text-gray-700">
                      Get consistent, high-quality results every time
                    </span>
                  </li>
                </ul>
                <div className="mt-8">
                  <GradientButton to="/signup">
                    Start Building Now
                  </GradientButton>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
                className="relative"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-yellow-400 to-yellow-300 opacity-50 rounded-2xl transform -rotate-2"></div>
                <div className="relative overflow-hidden rounded-xl shadow-2xl border-8 border-white">
                  <img
                    src={aiDemoImage}
                    alt="Custom AI Tool Demo"
                    className="w-full h-auto"
                  />
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Testimonials Section */}
        <section className="py-24 bg-gradient-to-b from-gray-50 to-white">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="text-center max-w-3xl mx-auto mb-16"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-5">
                What People Are Building
              </h2>
              <p className="text-xl text-gray-600 leading-relaxed">
                Hear from educators and professionals who are transforming their
                workflows with custom AI tools.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
              <Testimonial
                quote="I created a custom tool that analyzes student responses and automatically generates personalized feedback with suggested resources. What used to take me 30 minutes per assignment now happens in seconds—and the entire department has adopted it for consistent feedback across all courses."
                author="Emma Rodriguez"
                role="Education Technology Specialist"
                delay={0}
              />

              <Testimonial
                quote="My custom research summarizer tool extracts key information from academic papers and organizes it exactly how I need it. I've built a small library of specialized tools that have completely transformed my literature review process, saving me countless hours of work."
                author="Michael Chen"
                role="PhD Researcher"
                delay={1}
              />

              <Testimonial
                quote="I built a lesson plan generator that creates engaging activities based on our curriculum standards. It produces in 30 seconds what used to take me hours to develop manually. The best part is being able to tweak any aspect of the tool to match my exact teaching style."
                author="Sarah Johnson"
                role="High School Teacher"
                delay={2}
              />
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-24 bg-white">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="text-center max-w-3xl mx-auto mb-16"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-5">
                Frequently Asked Questions
              </h2>
              <p className="text-xl text-gray-600 leading-relaxed">
                Everything you need to know about creating custom AI tools.
              </p>
            </motion.div>

            <div className="max-w-3xl mx-auto">
              <FaqItem
                question="Do I need coding skills to build custom AI tools?"
                answer="Absolutely not! Our tool builder is designed to be completely no-code. You simply tell the AI what you want it to do using natural language instructions, and specify what information it needs. The system handles all the technical aspects behind the scenes."
                delay={0}
              />

              <FaqItem
                question="What kinds of tools can I create?"
                answer="The possibilities are virtually endless. You can create tools for content generation, data analysis, document summarization, feedback automation, assessment creation, research assistance, and much more. If you can describe what you want, you can likely build it as a custom tool."
                delay={1}
              />

              <FaqItem
                question="How long does it take to create a custom tool?"
                answer="Most users create their first functional tool in under 5 minutes. More complex tools with multiple variables might take 10-15 minutes to set up. Once created, they're saved in your library for instant use anytime."
                delay={2}
              />

              <FaqItem
                question="Can I modify my tools after creating them?"
                answer="Yes! You can edit any aspect of your custom tools at any time. As your needs evolve, you can refine the instructions, add new variables, change output formats, or update any other settings to improve your tools."
                delay={3}
              />

              <FaqItem
                question="How much time can custom AI tools save me?"
                answer="Our users report saving anywhere from 5 to 20+ hours per week, depending on their workflow and the types of tools they create. Tasks that previously took hours can often be completed in seconds with a well-designed custom tool."
                delay={4}
              />

              <FaqItem
                question="Can I share my custom tools with colleagues?"
                answer="Yes, tools can be shared with team members to ensure consistent workflows and results across your organization. This is especially valuable in educational settings where standardized feedback and assessment are important."
                delay={5}
              />
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-r from-yellow-50 to-yellow-100 relative overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute right-[10%] bottom-[10%] h-64 w-64 rounded-full bg-yellow-300 opacity-20 mix-blend-multiply blur-3xl filter"></div>
            <div className="absolute left-[5%] top-[10%] h-72 w-72 rounded-full bg-yellow-300 opacity-20 mix-blend-multiply blur-3xl filter"></div>
          </div>

          <div className="container mx-auto px-4 relative">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="max-w-4xl mx-auto text-center"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Ready to Create Your First Custom AI Tool?
              </h2>
              <p className="text-xl text-gray-700 mb-10 leading-relaxed max-w-3xl mx-auto">
                Join thousands of educators and professionals who are saving
                hours every week with their own personalized AI tools.
              </p>
              <GradientButton to="/signup" className="px-8 py-4 text-lg">
                Start Building for Free
              </GradientButton>
              <p className="text-gray-600 text-sm mt-6">
                No credit card required
              </p>
            </motion.div>
          </div>
        </section>

        {/* Footer */}
        <footer className="py-16 bg-white border-t border-gray-100">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="flex flex-col items-center text-center">
                <h3 className="text-gray-800 font-bold mb-4">Yippity</h3>
                <p className="text-gray-600 max-w-md mb-8">
                  Advanced AI tools for students and educators to create
                  effective study materials and automate repetitive tasks.
                </p>

                <div className="mt-4 pt-6 border-t border-gray-200 w-full flex flex-col items-center">
                  <p className="text-gray-500 text-sm mb-2">
                    © 2025 Yippity. All rights reserved.
                  </p>
                  <div className="flex space-x-4 mb-4">
                    <a
                      href="https://x.com/juan_two_juan"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gray-500 hover:text-gray-700 text-sm"
                    >
                      Twitter
                    </a>
                    <a
                      href="https://www.linkedin.com/in/juan-quintero-ovalle/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gray-500 hover:text-gray-700 text-sm"
                    >
                      LinkedIn
                    </a>
                  </div>
                  <div className="flex space-x-4">
                    <Link
                      to="/privacy"
                      className="text-gray-500 hover:text-gray-700 text-sm"
                    >
                      Privacy Policy
                    </Link>
                    <Link
                      to="/terms"
                      className="text-gray-500 hover:text-gray-700 text-sm"
                    >
                      Terms of Service
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </footer>
      </PageWrapper>
    </>
  );
};

export default CustomToolsLandingPage;
